import { t } from './i18n';

// Annuler / Refaire pour les modifications de l'éditeur.
//  - Les mutations « update… » d'un module ou d'une page sont enregistrées automatiquement (voir config.js) :
//    l'ancienne valeur est lue dans le cache Apollo AVANT l'envoi, et « annuler » renvoie la même mutation avec l'ancienne valeur.
//  - Les déplacements / redimensionnements passent par recordLayoutChange (PageEdition.js).
// Si une ancienne valeur est introuvable dans le cache, la modification n'est pas enregistrée plutôt que de risquer un mauvais retour en arrière.
const MAX_ENTRIES = 50;
const COALESCE_MS = 2000;

let revision = 0; // incrémenté après chaque annulation / rétablissement : permet de réafficher les formulaires
let undoStack = [];
let redoStack = [];
let replaying = false;
let applyMutation = null; // (mutation, variables) => Promise, fourni par config.js
let refreshAll = null;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(getHistoryState()));

export const getHistoryState = () => ({
  revision,
  canUndo: undoStack.length > 0,
  canRedo: redoStack.length > 0,
  undoLabel: undoStack.length ? undoStack[undoStack.length - 1].label : null,
  redoLabel: redoStack.length ? redoStack[redoStack.length - 1].label : null,
});

export function subscribeHistory(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function configureHistory({ mutate, refresh }) {
  applyMutation = mutate;
  refreshAll = refresh;
}

export const isReplaying = () => replaying;

// L'historique appartient à une interface : on le vide quand on en ouvre une autre (et pas quand l'éditeur se réaffiche).
let scope = null;
export function setHistoryScope(projectId) {
  if (scope !== null && scope !== projectId) clearHistory();
  scope = projectId;
}

export function clearHistory() {
  undoStack = [];
  redoStack = [];
  notify();
}

if (typeof window !== 'undefined') window.__tskHistoryDebug = () => ({ undo: undoStack.map((e) => e.label), redo: redoStack.map((e) => e.label), replaying });

function push(entry) {
  const last = undoStack[undoStack.length - 1];
  // Frappes rapides sur le même champ : une seule étape d'historique.
  if (last && last.key && last.key === entry.key && entry.at - last.at < COALESCE_MS) {
    last.redo = entry.redo;
    last.at = entry.at;
  } else {
    undoStack.push(entry);
    if (undoStack.length > MAX_ENTRIES) undoStack.shift();
  }
  redoStack = [];
  notify();
}

async function run(fn) {
  replaying = true;
  try {
    await fn();
    // Le rafraîchissement ne doit jamais faire échouer une annulation déjà appliquée (ex. requête d'un module supprimé).
    // Sans attendre : certaines requêtes actives peuvent ne jamais se terminer et bloqueraient tout l'historique.
    if (refreshAll) { try { Promise.resolve(refreshAll()).catch(() => {}); } catch (e) { /* ignore */ } }
  } finally {
    replaying = false;
    revision += 1;
  }
}

export async function undo() {
  const entry = undoStack.pop();
  if (!entry) return false;
  notify();
  try {
    await run(entry.undo);
    redoStack.push(entry);
  } catch (e) {
    console.error('Undo failed', e);
  }
  notify();
  return true;
}

export async function redo() {
  const entry = redoStack.pop();
  if (!entry) return false;
  notify();
  try {
    await run(entry.redo);
    undoStack.push(entry);
  } catch (e) {
    console.error('Redo failed', e);
  }
  notify();
  return true;
}

// Ajout (ou duplication) d'un module : annuler = supprimer le module, rétablir = le recréer (nouvel identifiant).
// `create()` doit retourner l'identifiant du module créé ; `remove(id)` supprime un module.
export function recordCellAddition({ label, cellId, create, remove }) {
  if (replaying || !applyMutation) return;
  let currentId = cellId;
  push({
    label,
    key: null,
    at: Date.now(),
    undo: () => remove(currentId),
    redo: async () => { currentId = await create(); },
  });
}

// Déplacement / redimensionnement : previous et next = tableaux [{cell_id, x, y, width, height}].
export function recordLayoutChange({ previous, next, screenTypeId, mutation }) {
  if (replaying || !applyMutation) return;
  push({
    label: t('ui.history.label.move'),
    key: null,
    at: Date.now(),
    undo: () => applyMutation(mutation, { input: previous, screenTypeId }),
    redo: () => applyMutation(mutation, { input: next, screenTypeId }),
  });
}

const unwrap = (value) => (value && value.type === 'json' ? value.json : value);
const isRef = (value) => value && typeof value === 'object' && (value.type === 'id' || value.generated !== undefined);

// Appelé par le lien Apollo avant l'envoi d'une mutation « update… » ; retourne une fonction à appeler si la mutation réussit.
export function prepareRecording(operation, cache) {
  if (replaying || !applyMutation) return null;
  const def = operation.query.definitions.find((d) => d.kind === 'OperationDefinition');
  const field = def && def.selectionSet.selections.find((s) => s.kind === 'Field');
  const name = field && field.name.value;
  const input = operation.variables && operation.variables.input;
  if (!name || !/^update/.test(name) || !input || Array.isArray(input) || !input.id) return null;
  if (name === 'updateProject' || name === 'updateDevice') return null;

  const data = cache.extract(false);
  const key = Object.keys(data).find((k) => k.endsWith(`:${input.id}`));
  const entity = key && data[key];
  if (!entity) return null;

  const oldInput = { id: input.id };
  const newInput = { id: input.id };
  const changed = [];
  for (const fieldName of Object.keys(input)) {
    if (fieldName === 'id') continue;
    if (!(fieldName in entity)) return null;
    const oldValue = unwrap(entity[fieldName]);
    if (isRef(oldValue)) return null;
    if (JSON.stringify(oldValue) === JSON.stringify(input[fieldName])) continue;
    oldInput[fieldName] = oldValue;
    newInput[fieldName] = input[fieldName];
    changed.push(fieldName);
  }
  if (changed.length === 0) return null;

  const mutation = operation.query;
  const variables = operation.variables;
  const entryKey = `${input.id}|${changed.sort().join(',')}`;
  return () => push({
    label: t('ui.history.label.change'),
    key: entryKey,
    at: Date.now(),
    undo: () => applyMutation(mutation, { ...variables, input: oldInput }),
    redo: () => applyMutation(mutation, { ...variables, input: newInput }),
  });
}
